// HD (2x) boss sprites for the art/perspective prototype: upright three-quarter figures with the feet on the bottom
// row, drawn in four views like the people (east is mirrored for west). Colors come from the hd-kit ramps only.
// Textures are cached by (kind, view, stride, pose, glow); the renderer shows them at scale 0.5.
import { Texture } from 'pixi.js';
import { canvas, tex } from './art';
import { K, px, dk, lt, mix, alpha, disc, speckle } from './hd-kit';
import { outline, type Facing } from './hd-sprites';
import { weaponCanvas } from './character-art';
import type { BossSnap } from '../../common/src/protocol';
type BossKind = BossSnap['k'];

/** texture size in hd px and how far below the hitbox centre (world px) the feet stand */
export const BOSS_DIM: Record<BossKind, { w: number; h: number; foot: number }> = {
  brute: { w: 96, h: 112, foot: 9 }, mech: { w: 128, h: 128, foot: 14 }, kaiju: { w: 240, h: 256, foot: 24 },
};
export interface BossPose { stride: number; raise: boolean; fire: boolean; glow: number; breath: number; tail: number }

/** a shaded slab: light on the left, shadow on the right and underneath */
function sb(g: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, c: string) {
  px(g, x, y, w, h, c); px(g, x, y, Math.min(2, w), h, lt(c, 0.2)); px(g, x + w - Math.min(2, w), y, Math.min(2, w), h, dk(c, 0.3)); px(g, x, y + h - 2, w, 2, dk(c, 0.22));
}

/** a rounded mass lit from the top-left: dark rim at the bottom-right, body, then a soft highlight */
function blob(g: CanvasRenderingContext2D, x: number, y: number, rx: number, ry: number, c: string, hi = 0) {
  rx = Math.round(rx); ry = Math.round(ry);
  disc(g, x, y, rx, ry, dk(c, 0.3));
  disc(g, x - 1, y - 2, Math.max(1, rx - 2), Math.max(1, ry - 2), c);
  if (hi > 0 && rx > 4 && ry > 4) disc(g, x - Math.round(rx * 0.3), y - Math.round(ry * 0.4), Math.round(rx * 0.45), Math.round(ry * 0.35), lt(c, hi));
}
/** an armour plate: top and left edges catch the light, right and bottom fall into shadow */
function plate(g: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, c: string) {
  px(g, x + 1, y, w - 2, h, c); px(g, x, y + 1, w, h - 2, c); px(g, x + 2, y, w - 4, 1, lt(c, 0.35)); px(g, x, y + 1, 1, h - 1, lt(c, 0.2));
  px(g, x + w - 1, y + 1, 1, h - 1, dk(c, 0.35)); px(g, x + 1, y + h - 1, w - 1, 1, dk(c, 0.45));
}
/** a rivet: a lit dot with its shadow below-right */
function rivet(g: CanvasRenderingContext2D, x: number, y: number, c: string) { px(g, x, y, 1, 1, lt(c, 0.5)); px(g, x + 1, y + 1, 1, 1, dk(c, 0.55)); }
/** diagonal amber/ink warning stripes */
function hazard(g: CanvasRenderingContext2D, x: number, y: number, w: number, h: number) {
  px(g, x, y, w, h, K.warmLo);
  for (let j = 0; j < h; j++) for (let i = 0; i < w; i++) if (((i + j) & 7) < 3) px(g, x + i, y + j, 1, 1, K.ink);
  px(g, x, y, w, 1, K.warm);
}
/** grime and rust only over pixels already drawn (source-atop), seeded so the texture is stable */
function weather(g: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, seed: number, dirt: string, rust = 0.02) {
  g.save(); g.globalCompositeOperation = 'source-atop';
  speckle(g, x, y, w, h, alpha(dirt, 0.5), 0.025, seed); if (rust) speckle(g, x, y, w, h, alpha(K.woodLo, 0.8), rust, seed + 3, 2);
  g.restore();
}

/** the untextured, outlined canvas for a boss view (bossTex wraps it; previews and tests can draw it directly) */
export function bossCanvas(kind: BossKind, view: Facing, p: BossPose): HTMLCanvasElement {
  const d = BOSS_DIM[kind], [c, g] = canvas(d.w, d.h);
  (kind === 'brute' ? brute : kind === 'mech' ? mech : kaiju)(g, view === 'W' ? 'E' : view, p, d.w, d.h);
  bossDetail(g,kind,view === 'W' ? 'E' : view,p,d.w);
  let out = outline(c);
  if (view === 'W') { const [m, mg] = canvas(d.w, d.h); mg.translate(d.w, 0); mg.scale(-1, 1); mg.drawImage(out, 0, 0); out = m; }
  return out;
}

const cache = new Map<string, {texture:Texture;users:number}>();
const entries=new WeakMap<Texture,{texture:Texture;users:number}>();
const BOSS_CACHE_LIMIT=192;
function pruneBossTextures(protectedKey?:string){
  for(const [key,e] of cache){if(cache.size<=BOSS_CACHE_LIMIT)break;if(e.users||key===protectedKey)continue;e.texture.destroy(true);cache.delete(key);}
}
export function retainBossTexture(t:Texture){const e=entries.get(t);if(e)e.users++;}
export function releaseBossTexture(t:Texture){const e=entries.get(t);if(e)e.users=Math.max(0,e.users-1);pruneBossTextures();}
export function bossTex(kind: BossKind, view: Facing, p: BossPose): Texture {
  const key = `${kind}${view}${p.stride}${p.raise ? 1 : 0}${p.fire ? 1 : 0}${p.glow}${p.breath}${p.tail}`;
  const hit = cache.get(key); if (hit) {cache.delete(key);cache.set(key,hit);return hit.texture;}
  const t = tex(bossCanvas(kind, view, p)), entry={texture:t,users:0};cache.set(key,entry);entries.set(t,entry);pruneBossTextures(key);return t;
}

// ------------------------------------------------------------------ Goro the Gate
function brute(g: CanvasRenderingContext2D, v: Facing, p: BossPose, W: number, H: number) {
  const cx=W/2,side=v==='E',back=v==='N',st=p.stride,feet=H-2;
  const coat=dk(K.slate,.38),hi=lt(coat,.16),deep=dk(coat,.55),skin=mix(K.amber,K.white,.4);
  const P=(points:number[][],c:string)=>{
    for(let y=Math.ceil(Math.min(...points.map(q=>q[1])));y<Math.ceil(Math.max(...points.map(q=>q[1])));y++){
      const cuts:number[]=[];for(let i=0;i<points.length;i++){const a=points[i],b=points[(i+1)%points.length];if((a[1]<=y+.5&&b[1]>y+.5)||(b[1]<=y+.5&&a[1]>y+.5))cuts.push(a[0]+(y+.5-a[1])/(b[1]-a[1])*(b[0]-a[0]));}
      cuts.sort((a,b)=>a-b);for(let i=0;i+1<cuts.length;i+=2)px(g,Math.round(cuts[i]),y,Math.round(cuts[i+1])-Math.round(cuts[i]),1,c);
    }
  };
  // Planted steel-toe boots, knee braces, and a split coat moving around the legs.
  for(const n of [-1,1]){
    const x=cx+(side?n*3:n*10)+st*n*2,lift=Math.max(0,n*st)*3;
    sb(g,x-5,73,11,feet-73-lift,deep);plate(g,x-5,87-lift,11,8,K.metalLo);
    px(g,x-3,88-lift,7,1,K.metalHi);plate(g,x-7,feet-6-lift,16,6,K.ink);px(g,x-6,feet-5-lift,13,1,K.metalHi);
  }
  const half=side?15:23;
  P([[cx-half,34],[cx+half,34],[cx+half+3,80],[cx+half+5+st*2,93],[cx+4,91],[cx,76],[cx-5,91],[cx-half-4-st*2,91]],coat);
  px(g,cx-half+2,37,2,43,hi);px(g,cx+half-3,36,2,43,deep);
  for(const n of [-1,1]){px(g,cx+n*(half-1),72,1,15,K.redLo);px(g,cx+n*10,78,1,10,deep);}
  px(g,cx-half,63,half*2,6,K.redDk);px(g,cx-half+1,63,half*2-2,2,K.red);
  plate(g,cx-4,63,8,6,K.warmLo);px(g,cx-2,64,4,2,K.amber);
  if(back){
    // Embroidered Oni-Gumi crest: an angular mask, no invented lettering.
    plate(g,cx-13,39,26,21,deep);P([[cx-10,43],[cx,40],[cx+10,43],[cx+7,55],[cx,59],[cx-7,55]],K.redLo);
    px(g,cx-7,45,5,2,K.amber);px(g,cx+2,45,5,2,K.amber);px(g,cx-4,53,8,2,K.white);
    px(g,cx-11,40,2,8,K.amber);px(g,cx+9,40,2,8,K.amber);
  }else{
    P([[cx-14,33],[cx-3,37],[cx-5,60],[cx-11,49]],deep);P([[cx+14,33],[cx+3,37],[cx+5,60],[cx+11,49]],hi);
    px(g,cx-2,37,4,23,K.ink);px(g,cx-1,39,1,17,K.metalHi);
    for(let i=0;i<3;i++){plate(g,cx+7,46+i*4,8,3,K.metalLo);px(g,cx+8,46+i*4,5,1,K.warm);}
    px(g,cx-14,52,8,2,K.red);px(g,cx-12,54,4,1,K.amber);
  }
  for(const n of [-1,1]){
    const x=cx+n*(side?12:24)-6;
    plate(g,x,31,14,12,hi);px(g,x+1,32,12,2,K.redLo);px(g,x+2,34,9,1,K.amber);
    rivet(g,x+2,39,coat);rivet(g,x+10,39,coat);
  }
  // Bent forearms are shorter than the torso. The giant gauntlet is the weapon, not an elongated arm.
  const gx=cx-(side?10:28),gy=p.raise?13:59;
  P([[gx-5,38],[gx+5,38],[gx+8,p.raise?26:52],[gx+5,gy+5],[gx-5,gy+5],[gx-8,p.raise?26:51]],hi);
  plate(g,gx-9,gy,19,17,K.metalLo);plate(g,gx-7,gy+2,15,11,K.metal);
  px(g,gx-5,gy+3,11,2,p.raise?K.white:K.red);px(g,gx-1,gy+6,3,6,K.red);
  for(let i=0;i<4;i++){plate(g,gx-7+i*4,gy+12,4,6,K.metalLo);px(g,gx-6+i*4,gy+12,2,1,K.amber);}
  for(const n of [-1,1]){px(g,gx+n*8,gy+4,1,7,K.red);rivet(g,gx+n*6,gy+8,hi);}
  const ax=cx+(side?12:24);
  P([[ax-5,38],[ax+5,39],[ax+6,54],[cx+9,63],[cx+3,58],[ax-3,49]],coat);
  plate(g,ax-5,47,10,8,K.metalLo);px(g,cx+3,57,8,7,skin);px(g,cx+3,62,8,2,dk(skin,.3));
  if(!back){g.save();g.translate(cx-1,56);g.rotate(side?-.1:.5);g.scale(.85,.85);g.drawImage(weaponCanvas('shotgun'),0,0);g.restore();}
  // Sculpted crimson oni face, swept ivory horns, jaw vents and topknot.
  const hx=cx-(side?7:12),hw=side?19:24;
  P([[hx+3,9],[hx+hw-3,9],[hx+hw,15],[hx+hw-1,29],[cx+3,35],[hx+3,30],[hx,15]],back?K.ink:K.redLo);
  px(g,cx-4,5,8,6,K.ink);px(g,cx-2,5,4,1,K.metalHi);
  if(!back){
    P([[hx+1,14],[hx-5,8],[hx-4,3],[hx+5,13]],K.white);P([[hx+hw-5,13],[hx+hw+4,3],[hx+hw+5,8],[hx+hw-1,15]],K.plaster);
    px(g,hx+3,13,hw-6,2,K.red);px(g,hx+2,17,hw-4,2,K.ink);
    for(const x of side?[hx+11]:[hx+4,hx+hw-9]){px(g,x,19,5,2,K.amber);px(g,x+2,19,2,1,K.white);}
    P([[cx,19],[cx+4,23],[cx+1,25],[cx-2,23]],K.red);px(g,hx+5,27,hw-10,3,K.ink);
    for(let i=0;i<3;i++)px(g,hx+6+i*4,27,2,2,K.white);
    px(g,hx+3,24,2,3,K.warmLo);px(g,hx+hw-5,24,2,3,K.warmLo);
  }else{px(g,hx+2,16,hw-4,3,K.metalLo);px(g,cx-2,18,4,13,K.ink);}
  if(p.fire&&!p.raise&&!back){const x=side?cx+39:cx+34,y=side?58:74;disc(g,x,y,5,3,K.amber);disc(g,x,y,2,2,K.white);}
}

// ------------------------------------------------------------------ Tetsu-Kamen Mech Walker
// A four-legged walker: layered, riveted armour over a hull, hydraulic legs, twin shoulder guns, a glowing reactor
// and a kabuki face plate with red kumadori lines under a gold crest.
function mech(g: CanvasRenderingContext2D, v: Facing, p: BossPose, W: number, H: number) {
  const cx = W / 2, side = v === 'E', back = v === 'N', st = p.stride, feet = H - 1;
  const hull = dk(K.slate, 0.12), armor = dk(K.slate, 0.3), armorLo = dk(K.slate, 0.5), seam = dk(K.slate, 0.62);
  const glow = [0.25, 0.5, 0.75, 1][p.glow], hot = mix(K.glassLo, K.cyan, glow), core = mix(K.cyan, K.white, glow * 0.7);
  const vent = (x: number, y: number, w: number, n: number) => {
    px(g, x - 1, y - 1, w + 2, n * 3 + 1, K.ink);
    for (let i = 0; i < n; i++) { px(g, x, y + i * 3 + 1, w, 1, hot); px(g, x + 2, y + i * 3 + 1, w - 4, 1, core); }
  };
  const reactor = (x: number, y: number, r: number) => {
    disc(g, x, y, r + 2, r + 2, K.ink); disc(g, x, y, r, r, hot); disc(g, x - 1, y - 1, Math.max(1, r - 3), Math.max(1, r - 3), core);
    for (let i = -r; i <= r; i += 4) px(g, x + i, y - r, 1, r * 2 + 1, alpha(K.ink, 0.7)); // cage bars
  };
  /** one leg: armoured thigh, hydraulic piston (sleeve and polished rod), knee joint, striped foot */
  const leg = (x: number, top: number, lift: number, c: string) => {
    const y0 = top - lift, fy = feet - 9 - lift, sy = y0 + 12, rodY = sy + Math.round((fy - sy) * 0.45);
    px(g, x + 3, sy, 7, fy - sy, K.ink); px(g, x + 4, sy, 5, rodY - sy, K.metalLo); px(g, x + 4, sy, 1, rodY - sy, K.metal);
    px(g, x + 5, rodY, 3, fy - rodY, K.metalHi); px(g, x + 5, rodY, 1, fy - rodY, K.white); px(g, x + 3, rodY - 2, 7, 2, K.ink);
    px(g, x - 1, sy + 2, 2, fy - sy - 2, K.metalLo); px(g, x + 12, sy + 2, 2, fy - sy - 2, dk(K.metalLo, 0.3)); // side struts
    plate(g, x, y0, 13, 14, c); px(g, x + 2, y0 + 5, 9, 1, dk(c, 0.45)); rivet(g, x + 2, y0 + 2, c); rivet(g, x + 9, y0 + 2, c);
    disc(g, x + 6, y0 + 14, 3, 3, K.ink); px(g, x + 5, y0 + 13, 2, 2, K.metalHi); // knee
    plate(g, x - 3, fy, 19, 9, c); hazard(g, x - 2, fy + 2, 17, 3); px(g, x - 3, fy + 7, 19, 2, K.ink);
  };
  // legs: rear pair (darker, higher) then front pair
  if (side) {
    leg(cx - 36 + st * 5, 70, Math.max(0, st) * 3, armorLo); leg(cx + 12 - st * 5, 70, Math.max(0, -st) * 3, armorLo);
    leg(cx - 26 - st * 5, 74, Math.max(0, -st) * 3, armor); leg(cx + 2 + st * 5, 74, Math.max(0, st) * 3, armor);
  } else {
    leg(cx - 50, 68, Math.max(0, st) * 3, armorLo); leg(cx + 37, 68, Math.max(0, -st) * 3, armorLo);
    leg(cx - 34 - st * 2, 72, Math.max(0, -st) * 3, armor); leg(cx + 21 + st * 2, 72, Math.max(0, st) * 3, armor);
  }

  if (side) {
    // hull seen from the side: deck plate, riveted skirt, reactor exhaust at the back
    const bx = cx - 40, bw = 70;
    plate(g, bx, 36, bw, 42, hull); plate(g, bx + 4, 31, bw - 8, 12, lt(hull, 0.08)); px(g, bx + 4, 42, bw - 8, 1, seam);
    plate(g, bx + 10, 46, 46, 22, armor); px(g, bx + 10, 46, 46, 1, K.red);
    for (let k = 0; k < 7; k++) { rivet(g, bx + 13 + k * 6, 49, armor); rivet(g, bx + 13 + k * 6, 64, armor); }
    px(g, bx + 32, 47, 1, 20, seam); vent(bx + 3, 47, 5, 4);
    hazard(g, bx + 2, 70, bw - 4, 5);
    weather(g, bx, 30, bw, 48, 21, K.ink);
    reactor(bx - 2, 58, 6);
  } else {
    const bw = 80, bx = cx - 40;
    plate(g, bx, 36, bw, 42, hull); plate(g, bx + 4, 30, bw - 8, 13, lt(hull, 0.08)); px(g, bx + 4, 42, bw - 8, 1, seam);
    px(g, bx + 24, 44, 1, 26, seam); px(g, bx + bw - 25, 44, 1, 26, seam); px(g, bx + 8, 60, bw - 16, 1, seam);
    for (const sx of [bx - 5, bx + bw - 9]) { // layered side skirts
      plate(g, sx, 42, 14, 30, armor); px(g, sx, 42, 14, 1, K.red);
      for (let k = 0; k < 4; k++) { rivet(g, sx + 3, 46 + k * 7, armor); rivet(g, sx + 9, 46 + k * 7, armor); }
    }
    hazard(g, bx + 4, 70, bw - 8, 5);
    if (back) { vent(bx + 12, 46, 10, 4); vent(bx + bw - 22, 46, 10, 4); reactor(cx, 56, 9); }
    else { vent(bx + 12, 47, 9, 3); vent(bx + bw - 21, 47, 9, 3); reactor(cx, 61, 7); }
    weather(g, bx - 5, 28, bw + 10, 50, back ? 31 : 41, K.ink);
  }

  // shoulder guns
  if (side) {
    const gy = 49;
    plate(g, cx - 10, gy - 3, 22, 13, armorLo); rivet(g, cx - 8, gy - 1, armorLo); rivet(g, cx + 8, gy - 1, armorLo);
    for (const by of [gy, gy + 5]) { px(g, cx + 12, by, 34, 3, K.ink); px(g, cx + 12, by, 34, 1, K.metalHi); px(g, cx + 42, by - 1, 4, 5, K.metalLo); }
    if (p.fire) { disc(g, cx + 50, gy + 3, 6, 6, K.amber); disc(g, cx + 50, gy + 3, 3, 3, K.white); }
    else { px(g, cx + 46, gy + 1, 2, 1, K.warm); px(g, cx + 46, gy + 6, 2, 1, K.warm); }
  } else {
    for (const x of [cx - 59, cx + 45]) {
      plate(g, x, 24, 14, 20, armorLo); hazard(g, x + 1, 25, 12, 2); rivet(g, x + 2, 30, armorLo); rivet(g, x + 10, 30, armorLo);
      if (back) { px(g, x + 3, 16, 3, 9, K.ink); px(g, x + 8, 16, 3, 9, K.ink); px(g, x + 3, 16, 1, 9, K.metalHi); px(g, x + 8, 16, 1, 9, K.metalHi); if (p.fire) disc(g, x + 7, 14, 5, 4, K.amber); continue; }
      for (const bx of [x + 2, x + 8]) { px(g, bx, 43, 4, 12, K.ink); px(g, bx, 43, 1, 12, K.metalHi); px(g, bx, 53, 4, 2, K.metalLo); }
      if (p.fire) { disc(g, x + 7, 60, 8, 6, K.amber); disc(g, x + 7, 59, 4, 3, K.white); }
      else { px(g, x + 3, 55, 2, 1, K.warm); px(g, x + 9, 55, 2, 1, K.warm); }
    }
  }

  // head: helmet with a gold maedate crest; the kabuki face plate in front, red cords at the back
  const crest = (x: number, y: number, n: number, dir: number) => {
    for (let i = 0; i < n; i++) { const hx = x + dir * i * 2 - (dir < 0 ? 2 : 0); px(g, hx, y - i, 3, 2, i > n - 3 ? K.warmHi : K.warm); px(g, hx, y - i + 2, 3, 1, K.warmLo); }
  };
  if (side) {
    const hx = cx + 10, hy = 10;
    plate(g, hx, hy + 6, 30, 34, armorLo); plate(g, hx - 2, hy + 4, 26, 7, armor); px(g, hx - 2, hy + 10, 26, 1, K.red);
    crest(hx + 18, hy + 4, 8, 1); disc(g, hx + 17, hy + 6, 2, 2, K.red);
    const fx = hx + 18, fy = hy + 11;
    px(g, fx, fy, 14, 28, K.white); px(g, fx, fy, 1, 28, mix(K.white, K.plaster, 0.4)); px(g, fx, fy + 24, 4, 4, armorLo); // profile plate
    px(g, fx + 6, fy + 9, 7, 3, K.ink); px(g, fx + 10, fy + 10, 2, 1, K.pink); // eye slit
    for (let i = 0; i < 7; i++) px(g, fx + 12 - i * 2, fy + 7 - (i >> 1), 2, 2, K.red); // brow sweeping back
    for (const [dx, dy] of [[5, 14], [4, 15], [4, 16], [5, 17], [6, 18]]) px(g, fx + dx, fy + dy, 2, 1, K.red); // cheek line
    px(g, fx + 12, fy + 12, 2, 6, mix(K.white, K.plaster, 0.5)); // nose ridge
    px(g, fx + 7, fy + 22, 7, 3, K.red); px(g, fx + 8, fy + 23, 6, 1, K.ink);
    for (let k = 0; k < 3; k++) px(g, hx + 3, hy + 16 + k * 6, 10, 2, K.ink); // neck pistons
    weather(g, hx - 2, hy + 4, 32, 36, 51, K.ink, 0);
  } else if (back) {
    const hx = cx - 17, hy = 8;
    plate(g, hx, hy + 4, 34, 34, armorLo); plate(g, hx + 3, hy + 6, 28, 10, armor); px(g, hx + 3, hy + 16, 28, 1, K.red);
    crest(cx - 2, hy + 6, 8, -1); crest(cx + 2, hy + 6, 8, 1);
    px(g, cx - 10, hy + 20, 20, 8, K.ink); for (let i = 0; i < 5; i++) px(g, cx - 9 + i * 4, hy + 21, 2, 6, hot); // rear intake
    for (const tx of [cx - 13, cx + 11]) { px(g, tx, hy + 30, 2, 16, K.red); px(g, tx - 1, hy + 44, 4, 4, K.redLo); } // tassels
    weather(g, hx, hy, 34, 38, 61, K.ink, 0.01);
  } else {
    const hx = cx - 17, hy = 8;
    plate(g, hx, hy + 4, 34, 36, armorLo);
    plate(g, hx - 4, hy + 14, 7, 20, armor); plate(g, hx + 31, hy + 14, 7, 20, armor); // cheek guards
    crest(cx - 3, hy + 6, 9, -1); crest(cx + 2, hy + 6, 9, 1);
    const fx = cx - 13, fy = hy + 9, fw = 26, fh = 30, shade = mix(K.white, K.plaster, 0.45);
    px(g, fx, fy, fw, fh, K.white); px(g, fx + fw - 4, fy, 4, fh, shade); px(g, fx + 1, fy + fh - 2, fw - 2, 2, shade);
    px(g, fx, fy + fh - 5, 3, 5, armorLo); px(g, fx + fw - 3, fy + fh - 5, 3, 5, armorLo); // tapered chin
    plate(g, hx - 1, hy + 4, 36, 6, armor); px(g, hx - 1, hy + 9, 36, 1, K.red); disc(g, cx, hy + 6, 2, 2, K.red); // brow ridge, jewel
    for (const s of [-1, 1]) {
      const e = s < 0 ? cx - 10 : cx + 3;
      px(g, e, fy + 10, 7, 3, K.ink); px(g, e + (s < 0 ? 3 : 2), fy + 11, 2, 1, K.pink); // eye slit and glow
      for (let i = 0; i < 7; i++) px(g, Math.round(cx + s * (3 + i * 1.5) - (s < 0 ? 2 : 0)), fy + 7 - (i >> 1), 2, 3, K.red); // brows sweeping up
      px(g, s < 0 ? e - 1 : e + 7, fy + 9, 1, 5, K.ink); // eye corner flick
      for (const [dx, dy] of [[10, 14], [11, 15], [11, 16], [10, 17], [9, 18], [8, 19]]) px(g, cx + s * dx - (s < 0 ? 2 : 0), fy + dy, 2, 1, K.red); // cheek lines
      for (const [dx, dy] of [[12, 9], [13, 7], [12, 5]]) px(g, cx + s * dx - (s < 0 ? 1 : 0), fy + dy, 1, 2, K.redLo); // temple flick
    }
    px(g, cx, fy + 12, 2, 7, shade); // nose ridge
    px(g, cx - 5, fy + 22, 10, 3, K.red); px(g, cx - 4, fy + 23, 8, 1, K.ink); px(g, cx - 6, fy + 24, 1, 2, K.red); px(g, cx + 5, fy + 24, 1, 2, K.red);
    px(g, cx - 3, fy + 27, 6, 1, shade);
    weather(g, hx - 4, hy + 4, 42, 36, 71, K.ink, 0);
  }
}

// ------------------------------------------------------------------ NeonG
// Upright and leaning forward, about 120 world px from feet to head so it fits above the player at the default camera.
function kaiju(g: CanvasRenderingContext2D, v: Facing, p: BossPose, W: number, H: number) {
  const cx = W / 2, side = v === 'E', back = v === 'N', feet = H - 1, st = p.stride, hot = [0.3, 0.5, 0.75, 1][p.glow];
  const hide = dk(K.cyan, 0.8), hideHi = dk(K.cyan, 0.7), hideLo = dk(K.cyan, 0.88);
  const belly = mix(dk(K.cyan, 0.62), K.plaster, 0.3), bellyLo = dk(belly, 0.3);
  const plateC = mix(K.glassLo, K.cyan, hot), plateHi = mix(K.cyan, K.white, hot * 0.6);
  /** a glowing dorsal plate pointing up from (x, y) */
  const fin = (x: number, y: number, w: number, h: number) => {
    w = Math.round(w); h = Math.round(h); x = Math.round(x); y = Math.round(y);
    for (let j = 0; j < h; j++) { const ww = Math.max(1, Math.round(w * (1 - j / h))); px(g, x - (ww >> 1) - 1, y - j - 1, ww + 2, 2, K.ink); }
    for (let j = 0; j < h; j++) { const ww = Math.max(1, Math.round(w * (1 - j / h)) - 1); px(g, x - (ww >> 1), y - j, ww, 1, plateC); px(g, x - (ww >> 1), y - j, Math.max(1, ww >> 1), 1, plateHi); }
    for(let j=2;j<h-2;j++){px(g,x+(Math.floor(j/5)%2),y-j,1,1,hot>.7?K.white:K.cyan);if(j%6===0)px(g,x-3,y-j,6,1,plateHi);}
    px(g,x-Math.floor(w/2),y-2,w,2,hideLo);
  };
  const scales = (x: number, y: number, w: number, h: number, seed: number) => {
    g.save();g.globalCompositeOperation='source-atop';
    for(let row=0;row<h;row+=7)for(let col=0;col<w;col+=8){
      const sx=x+col+((row/7+seed)%2)*4,sy=y+row;
      px(g,sx,sy,5,1,hideHi);px(g,sx-1,sy+1,1,3,hideHi);px(g,sx+5,sy+1,1,3,hideLo);px(g,sx,sy+4,5,1,hideLo);
      if((col/8+row/7+seed)%5===0)px(g,sx+1,sy+1,2,1,lt(hideHi,.13));
    }
    g.restore();
  };
  /** belly plates: horizontal seams across an oval */
  const bellyOval = (x: number, y: number, rx: number, ry: number) => {
    blob(g, x, y, rx, ry, belly, 0.12);
    for (let dy = -ry + 4; dy < ry; dy += 5) { const hw = Math.round(rx * Math.sqrt(Math.max(0, 1 - (dy * dy) / (ry * ry)))) - 3; if (hw > 2) px(g, x - hw, y + dy, hw * 2, 1, bellyLo); }
  };
  const claws = (x: number, y: number, n: number, gap: number) => { for (let k = 0; k < n; k++) { px(g, x + k * gap, y, 3, 3, K.white); px(g, x + k * gap + 2, y + 1, 1, 2, K.plaster); } };

  const open = p.breath ? 6 : 0;
  if (side) {
    const sw = p.tail;
    // tail: heavy at the hips, sweeping back to the ground, tip swinging
    for (let i = 14; i >= 0; i--) {
      const t = i / 14, x = Math.round(cx - 22 - t * 86), y = Math.round(176 + t * 62 + sw * t * t * 0.8), r = 24 - t * 18;
      blob(g, x, y, r + 2, r, i % 2 ? hide : dk(hide, 0.08));
      if (i % 2 === 0 && i < 13 && i > 0) fin(x, y - r + 3, 10 - t * 5, 12 - t * 6);
    }
    // far leg and far arm
    blob(g, cx - 18 + st * 4, 184, 20, 26, hideLo); blob(g, cx - 12 + st * 4, 222, 13, 22, hideLo); blob(g, cx - 6 + st * 4, feet - 5, 20, 6, dk(hideLo, 0.3));
    blob(g, cx + 26, 116, 6, 11, hideLo);
    // upright body leaning forward: hips, chest, shoulders
    blob(g, cx - 10, 166, 34, 36, hide); blob(g, cx + 6, 124, 32, 38, hide, 0.1); blob(g, cx + 16, 92, 25, 26, hide);
    scales(cx - 48, 60, 100, 150, 7);
    bellyOval(cx + 20, 142, 15, 36);
    // dorsal ridge along the back, tallest between the shoulders
    const ridge: [number, number, number][] = [[-38, 186, 12], [-40, 168, 16], [-38, 150, 20], [-32, 132, 24], [-24, 114, 28], [-14, 98, 30], [-4, 84, 26], [6, 72, 20]];
    for (const [dx, y, h] of ridge) fin(cx + dx, y, 12, h);
    // near leg
    const lx = cx - 2 - st * 4;
    blob(g, lx, 186, 26, 30, hide, 0.1); blob(g, lx + 8, 224, 15, 22, hide); blob(g, lx + 14, feet - 5, 24, 6, hideLo); claws(lx + 26, feet - 4, 3, 5);
    // small forearm
    blob(g, cx + 36, 116, 8, 12, hideHi); blob(g, cx + 44, 128, 6, 8, hide); claws(cx + 42, 134, 3, 4);
    // strong neck and the head thrust forward
    blob(g, cx + 30, 74, 19, 20, hide);
    blob(g, cx + 54, 66 + open, 22, 7, hideLo); // lower jaw
    if (p.breath) { px(g, cx + 42, 61, 34, open + 4, mix(K.cyan, K.white, 0.5)); px(g, cx + 46, 63, 30, open, K.white); }
    for (let k = 0; k < 6; k++) px(g, cx + 46 + k * 5, 62 + open, 2, 3, K.white);
    blob(g, cx + 52, 50, 25, 14, hideHi, 0.2); blob(g, cx + 70, 55, 13, 8, hideHi, 0.15);
    px(g, cx + 44, 60, 36, 2, K.ink); for (let k = 0; k < 7; k++) px(g, cx + 46 + k * 5, 62, 2, 3, K.white);
    px(g, cx + 79, 52, 2, 2, K.ink);
    px(g, cx + 50, 42, 13, 3, hideLo); px(g, cx + 54, 45, 6, 4, K.pink); px(g, cx + 55, 45, 2, 2, K.white);
    fin(cx + 38, 42, 8, 11); fin(cx + 46, 38, 7, 8);
    if (p.breath) { disc(g, cx + 82, 64, 8, 6, alpha(K.cyan, 0.8)); disc(g, cx + 82, 64, 4, 3, K.white); px(g, cx + 82, 61, W - cx - 82, 7, alpha(K.white, 0.85)); }
  } else {
    // front and back share the frame: legs apart, hips, a chest tapering up to the shoulders, a ridge of plates
    const lift = (s: number) => Math.max(0, s * st) * 3;
    if (back) {
      blob(g, cx, 64, 20, 18, hideHi, 0.15); // back of the head
    } else {
      for (let i = 9; i >= 0; i--) { const t = i / 9, r = 16 - t * 11; blob(g, Math.round(cx + 30 + t * 74), Math.round(226 + t * 18 + p.tail * t * 0.3), r + 2, r * 0.8, i % 2 ? hideLo : dk(hideLo, 0.15)); }
    }
    // ridge silhouette rising behind the shoulders and head
    const rh = [22, 32, 42, 48, 42, 32, 22];
    for (let i = 0; i < rh.length; i++) fin(cx - 36 + i * 12, 86 + Math.abs(i - 3) * 2, 14, rh[i]);
    for (const s of [-1, 1]) { blob(g, cx + s * 26, 190 - lift(s), 22, 30, hide, 0.08); blob(g, cx + s * 30, 226 - lift(s), 15, 22, hide); blob(g, cx + s * 32, feet - 5 - lift(s), 22, 6, hideLo); if (!back) claws(cx + s * 32 - 9, feet - 4 - lift(s), 3, 7); }
    blob(g, cx, 174, 40, 30, hide);
    blob(g, cx, 128, 42, 44, hide, 0.1);
    for (const s of [-1, 1]) blob(g, cx + s * 30, 96, 18, 18, hide);
    blob(g, cx, 86, 20, 16, hide); // neck
    scales(cx - 64, 70, 128, 140, back ? 9 : 3);
    for (const s of [-1, 1]) { blob(g, cx + s * 46, 118, 9, 16, hide); blob(g, cx + s * 42, 140, 7, 9, hideHi); if (!back) claws(cx + s * 42 - 5, 146, 3, 4); }
    if (back) {
      for (let r = 0; r < 7; r++) { fin(cx, 112 + r * 14, 14, 26 - r * 2); for (const s of [-1, 1]) fin(cx + s * 15, 118 + r * 14, 10, 15 - r); }
      for (let i = 0; i < 8; i++) { const t = i / 7, r = 26 - t * 10; blob(g, Math.round(cx + p.tail * t * 0.8), Math.round(196 + t * 46), r + 2, r * 0.7, i % 2 ? hide : dk(hide, 0.08)); }
      fin(cx + p.tail * 0.4, 214, 10, 12); fin(cx + p.tail * 0.6, 232, 8, 9);
    } else {
      bellyOval(cx, 150, 24, 42);
      // head from the front: heavy brow, glowing eyes, a muzzle narrowing to the nostrils, a defined jaw
      blob(g, cx, 88 + open, 21, 8, hideLo);
      if (p.breath) { px(g, cx - 14, 80, 28, open + 6, mix(K.cyan, K.white, 0.5)); px(g, cx - 10, 82, 20, open + 2, K.white); }
      for (let k = 0; k < 6; k++) px(g, cx - 13 + k * 5, 84 + open, 3, 3, K.white);
      blob(g, cx, 58, 24, 17, hideHi, 0.2); blob(g, cx, 75, 18, 10, hideHi, 0.12);
      px(g, cx - 15, 81, 30, 2, K.ink); for (let k = 0; k < 6; k++) px(g, cx - 13 + k * 5, 82, 3, 3, K.white);
      px(g, cx - 5, 74, 2, 2, K.ink); px(g, cx + 3, 74, 2, 2, K.ink);
      for (const s of [-1, 1]) { const ex = cx + s * 10 - 4; px(g, ex - 2, 49, 12, 3, hideLo); px(g, ex, 52, 8, 4, K.pink); px(g, ex + (s < 0 ? 1 : 5), 52, 2, 2, K.white); }
      if (p.breath) { disc(g, cx, 88, 10, 6, alpha(K.cyan, 0.7)); disc(g, cx, 88, 5, 3, K.white); }
    }
  }
}

/** Material accents follow the existing anatomy; their light strengthens with attack charge. */
function bossDetail(g:CanvasRenderingContext2D,kind:BossKind,v:Facing,p:BossPose,W:number){
  const cx=W/2,side=v==='E',hot=mix(K.glassLo,K.cyan,.3+p.glow*.2);
  if(kind==='brute'){
    // Density pass (2026-10-14): drawn only over existing pixels, so the silhouette and footprint stay the same.
    const back=v==='N',coat=dk(K.slate,.38),half=side?15:23,st=p.stride;
    g.save();g.globalCompositeOperation='source-atop';
    // coat: quilted stitch rows, a darker fold line per panel, worn lit edges on the hem
    for(let y=40;y<88;y+=6)for(let x=cx-half+3;x<cx+half-3;x+=4)if(((x+y)>>2)%2)px(g,x,y,2,1,lt(coat,.08));
    for(const n of [-1,1]){px(g,cx+n*(half-6),44,1,18,dk(coat,.5));px(g,cx+n*(half-6)+1,44,1,18,lt(coat,.12));}
    for(let x=cx-half-3;x<cx+half+4;x+=3)px(g,x,89+(x&1),2,1,lt(coat,.25));
    // sash: a woven pattern and two tassels
    for(let x=cx-half+1;x<cx+half-1;x+=3)px(g,x,66,1,2,K.redLo);
    px(g,cx-half+1,65,half*2-2,1,lt(K.red,.25));
    // boots: laces and a scuffed toe cap
    for(const n of [-1,1]){const x=cx+(side?n*3:n*10)+st*n*2,lift=Math.max(0,n*st)*3;for(let k=0;k<3;k++)px(g,x-2,76+k*3-lift,5,1,K.metalLo);px(g,x-5,BOSS_DIM.brute.h-8-lift,3,1,K.white);}
    if(!back){
      // gold chain on the chest, tattoo flecks on the fist, a scar across the mask's brow, shading under the horns
      for(let i=0;i<9;i++)px(g,cx-10+i*2+(i&1),38+Math.round(Math.abs(i-4)*1.5),1,1,i&1?K.warm:K.amber);
      px(g,cx+4,59,1,1,K.cyan);px(g,cx+6,60,2,1,K.cyan);px(g,cx+9,58,1,1,dk(K.cyan,.3));
      const hx=cx-(side?7:12),hw=side?19:24;
      px(g,hx+5,11,1,1,K.white);px(g,hx+6,12,1,1,K.white);px(g,hx+7,13,1,1,dk(K.white,.3));
      px(g,hx+2,15,hw-4,1,dk(K.redLo,.4));px(g,hx+hw-3,16,2,12,dk(K.redLo,.35));
      for(let i=0;i<3;i++)px(g,hx+6+i*4,29,2,1,dk(K.white,.3));
      // gauntlet: knuckle rivets lit, a warning stripe across the cuff
      const gx=cx-(side?10:28),gy=p.raise?13:59;
      for(let i=0;i<4;i++)px(g,gx-6+i*4,gy+16,2,1,K.metalHi);
      for(let i=0;i<15;i+=3)px(g,gx-7+i,gy+1,2,1,K.amber);
    }else{
      // back: the crest's embroidery stitched in a ring, a braided topknot cord
      for(let a=0;a<24;a++){const t=a/24*Math.PI*2;px(g,Math.round(cx+Math.cos(t)*12),Math.round(50+Math.sin(t)*10),1,1,a&1?K.amber:K.warmLo);}
      for(let k=0;k<5;k++)px(g,cx-1+(k&1),12+k*3,2,2,k&1?K.red:K.redLo);
    }
    g.restore();
  }
  if(kind==='mech'){
    for(const n of [-1,1]){
      const x=cx+n*(side?22:31)-3;
      plate(g,x,34,7,8,K.metalLo);px(g,x+1,35,5,1,K.metalHi);
      for(let i=0;i<3;i++)px(g,x+1,38+i,5,1,i===p.glow?K.white:hot);
      px(g,x+2,61,2,10,K.ink);px(g,x+3,61,1,8,K.redLo);
    }
    // Rear exhaust pipes, layered fasteners and weapon heat sinks.
    if(v==='N'){for(const x of [cx-29,cx+23]){plate(g,x,23,6,14,K.metalLo);px(g,x+1,24,4,3,K.ink);px(g,x+1,33,4,2,hot);}}
    if(side){for(let i=0;i<5;i++){px(g,cx+16+i*5,47,2,2,K.metal);px(g,cx+16+i*5,53,2,2,K.metal);}plate(g,cx-23,48,13,12,K.metalLo);px(g,cx-21,50,9,2,hot);}
    for(let i=0;i<5;i++)px(g,cx-15+i*6,74,3,1,K.metalHi);
    // Density pass: hull panel bevels, a hose bundle, unit number dashes and chipped paint, only over drawn pixels.
    g.save();g.globalCompositeOperation='source-atop';
    const hull=dk(K.slate,.12);
    for(let y=48;y<70;y+=7)for(let x=cx-36;x<cx+36;x+=9){px(g,x,y,7,1,lt(hull,.14));px(g,x,y+5,7,1,dk(hull,.4));}
    if(!side&&v!=='N'){for(let i=0;i<4;i++)px(g,cx-20+i*3,64,2,1,K.white);px(g,cx+10,64,6,1,K.red);}
    for(let i=0;i<14;i++){const x=cx-34+((i*23)%68),y=46+((i*17)%26);px(g,x,y,1,1,K.metalHi);px(g,x+1,y,1,1,K.ink);}
    g.restore();
    // hoses from the hull to the shoulder guns (they sit inside the gun mounts' outline)
    if(!side)for(const x of v==='N'?[cx-26,cx+24]:[cx-46,cx+44]){for(let k=0;k<6;k++)px(g,x+(k&1),40+k*2,2,2,k&1?K.ink:K.redLo);}
  }
  if(kind==='kaiju'){
    g.save();g.globalCompositeOperation='source-atop';
    // Bioelectric channels climb the ribs toward the dorsal array.
    for(const n of side?[-1]:[-1,1])for(let rib=0;rib<5;rib++){
      const bx=cx+n*(side?12:25),by=111+rib*12;
      for(let j=0;j<8;j++){const x=bx+n*j,y=by-Math.floor(j/2);px(g,x,y,2,1,dk(hot,.15));if(p.glow>1&&j<4)px(g,x,y,1,1,K.cyan);}
    }
    // Repeated skull scutes, cheek ridges and old pale battle scars.
    if(side){for(let i=0;i<4;i++){plate(g,cx+32+i*8,37+i%2,7,4,dk(K.cyan,.66));}px(g,cx+63,47,1,8,K.plaster);px(g,cx+65,50,1,7,dk(K.plaster,.3));}
    else if(v==='S'){for(const n of [-1,1]){plate(g,cx+n*13-4,42,8,4,dk(K.cyan,.66));px(g,cx+n*20,60,1,10,dk(K.plaster,.3));}}
    // Density pass: fine pebble scales between the big ones, claw and tooth shading, a lit pupil slit.
    for(let i=0;i<220;i++){const x=cx-60+((i*37)%120),y=70+((i*53)%170);px(g,x,y,1,1,i%3?dk(K.cyan,.74):dk(K.cyan,.9));}
    if(v==='S'){for(let k=0;k<6;k++){px(g,cx-13+k*5,84,1,1,K.plaster);px(g,cx-11+k*5,86,1,1,dk(K.plaster,.35));}for(const s of [-1,1])px(g,cx+s*10-1,52,1,4,K.ink);}
    if(side){for(let k=0;k<7;k++)px(g,cx+47+k*5,64,1,1,K.plaster);px(g,cx+58,45,1,3,K.ink);}
    g.restore();
  }
}
